/**
 * Grand-total math for the public proposal page and admin "remove install after sign"
 * — kept in one module to avoid drift.
 */
import {
  applyProfitMargin,
  getEffectiveProjectOverheadPercent,
  getGlobalCostAddonRawForNode,
  getProposalDiscountAmount,
} from './proposalCalculations';
import { effectiveGlobalCostsTotal, estimatingRaw } from './proposalDesignBreakdown';
import type { ProposalBuilderData, ProposalNodeSet } from '../types';
import {
  isStructureClientSelectable,
  normalizeExclusiveGroupSelections,
} from './proposalExclusiveGroups';
import { applySpeciesTimberKitBillableDelta } from './proposalSpeciesProduction';
import { computeBondingAmount, effectiveBondingPercent } from './proposalBonding';

/**
 * Phased Project mode: structure inclusion is driven by phase commitment for
 * EVERY structure (not just `clientOptional` ones), still expressed through the
 * same `structureSelections` map. Defined inline (two field reads) to avoid an
 * import cycle with `proposalPhases.ts`.
 */
function proposalIsPhased(
  proposal: Pick<ProposalBuilderData, 'phasingEnabled' | 'phases'>,
): boolean {
  return !!proposal.phasingEnabled && (proposal.phases?.length || 0) > 0;
}

/** Whether a structure counts toward client-facing pricing and display. */
export function isStructureClientSelected(
  ns: ProposalNodeSet,
  structureSelections?: Record<string, boolean>,
  /** Phased proposals: selections apply to all structures, not just optional ones. */
  phased?: boolean,
): boolean {
  if (ns.enabled === false) return false;
  if (!structureSelections) return true;
  if (!phased && !isStructureClientSelectable(ns)) return true;
  return structureSelections[ns.id] !== false;
}

/** Enabled structures included in client totals (respects optional deselection). */
export function getClientVisibleNodeSets(
  proposal: ProposalBuilderData,
  structureSelections?: Record<string, boolean>,
): ProposalNodeSet[] {
  const signedSnapshot = getAppliedSignedStructureSnapshot(proposal);
  if (signedSnapshot) {
    // Only ids explicitly included at signing. A structure added later has no key,
    // and a required structure must not fall through as "always included".
    return (proposal.nodeSets || []).filter(
      ns => ns.enabled !== false && signedSnapshot[ns.id] === true,
    );
  }
  const phased = proposalIsPhased(proposal);
  return (proposal.nodeSets || []).filter(ns =>
    isStructureClientSelected(ns, structureSelections, phased),
  );
}

function proposalIsSigned(
  proposal: Pick<ProposalBuilderData, 'status'> & { signature?: unknown },
): boolean {
  return proposal.status === 'approved' || !!proposal.signature;
}

/**
 * Per-structure inclusion written at signing.
 * Null when the proposal is unsigned, or was signed before this map existed.
 */
export function getSignedStructureSelectionSnapshot(
  proposal: ProposalBuilderData,
): Record<string, boolean> | null {
  if (!proposalIsSigned(proposal)) return null;
  const parsed = parseStructureSelectionsForPublicTotals(proposal);
  if (!parsed || Object.keys(parsed).length === 0) return null;
  return parsed;
}

/**
 * Sign-time map when it still names at least one structure on the proposal.
 * A map that matches nothing live is ignored so a legacy partial snapshot
 * cannot blank the proposal.
 */
export function getAppliedSignedStructureSnapshot(
  proposal: ProposalBuilderData,
): Record<string, boolean> | null {
  const snapshot = getSignedStructureSelectionSnapshot(proposal);
  if (!snapshot) return null;
  const liveIds = new Set((proposal.nodeSets || []).map(ns => ns.id));
  const namesLiveStructure = Object.keys(snapshot).some(id => liveIds.has(id));
  return namesLiveStructure ? snapshot : null;
}

/** Drop structures that were not on the proposal when it was signed. */
export function filterToSignedProposalLayout<T extends { id: string }>(
  proposal: ProposalBuilderData,
  nodeSets: T[],
): T[] {
  const snapshot = getAppliedSignedStructureSnapshot(proposal);
  if (!snapshot) return nodeSets;
  return nodeSets.filter(ns => ns.id in snapshot);
}

/** Sorted included-structure ids. Stored with the signed PDF so a later add does not rebuild it. */
export function signedIncludedStructureLayoutKey(
  selections: Record<string, boolean> | null | undefined,
): string | null {
  if (!selections) return null;
  const ids = Object.keys(selections).filter(id => selections[id] === true).sort();
  return ids.length ? ids.join('|') : null;
}

/**
 * The stored signed PDF is safe to download when it was rendered from the
 * sign-time structure set. A file saved before that key existed is not safe
 * once structures have been added — those downloads were rebuilt from the live list.
 */
export function signedPdfCacheMatchesContractLayout(proposal: ProposalBuilderData): boolean {
  const snapshot = getAppliedSignedStructureSnapshot(proposal);
  if (!snapshot) return true;
  const addedAfterSign = (proposal.nodeSets || []).some(
    ns => ns.enabled !== false && !(ns.id in snapshot),
  );
  if (!addedAfterSign) return true;
  const key = signedIncludedStructureLayoutKey(snapshot);
  return !!key && proposal.signedPdfLayoutKey === key;
}

export function proposalHasClientOptionalStructures(proposal: ProposalBuilderData): boolean {
  return (proposal.nodeSets || []).some(ns => ns.enabled !== false && ns.clientOptional === true);
}

export function proposalHasClientSelectableStructures(proposal: ProposalBuilderData): boolean {
  return (proposal.nodeSets || []).some(ns => ns.enabled !== false && isStructureClientSelectable(ns));
}

/** True when client has deselected at least one optional structure — requires dynamic global/OH recompute. */
export function needsDynamicClientPricing(
  proposal: ProposalBuilderData,
  structureSelections?: Record<string, boolean>,
): boolean {
  if (!structureSelections) return false;
  if (proposalIsPhased(proposal)) {
    // Any excluded (uncommitted-phase) structure forces the dynamic recompute
    // so global costs / project overhead spread across committed structures only.
    return (proposal.nodeSets || []).some(
      ns => ns.enabled !== false && structureSelections[ns.id] === false,
    );
  }
  if (!proposalHasClientSelectableStructures(proposal)) return false;
  return (proposal.nodeSets || []).some(
    ns => ns.enabled !== false && isStructureClientSelectable(ns) && structureSelections[ns.id] === false,
  );
}

function getProjectOverheadPerStructure(
  proposal: ProposalBuilderData,
  selectedNodeSets: ProposalNodeSet[],
): number {
  const pct = getEffectiveProjectOverheadPercent(proposal.globalCosts);
  if (pct <= 0 || selectedNodeSets.length === 0) return 0;

  const globalCostsTotal = effectiveGlobalCostsTotal(
    proposal.globalCosts?.totalGlobalCosts,
    proposal.designEstimating,
    null,
    proposal.status,
  );
  let structureRaw = 0;
  selectedNodeSets.forEach(ns => {
    const qty = ns.quantity || 1;
    structureRaw +=
      (ns.design.total +
        ns.production.total +
        ns.materials.total +
        ns.freight.total +
        ns.overhead.total +
        ns.install.total +
        ns.travel.total +
        (ns.install.overheadTotal || 0)) *
      qty;
  });
  const baseForProjectOverhead = structureRaw + globalCostsTotal;
  const projectOverheadAmount = Math.round(baseForProjectOverhead * (pct / 100));
  return projectOverheadAmount / selectedNodeSets.length;
}

/** Signed/approved proposals: follow Firestore (`installSelection` / species snapshot), including after admin removes install. */
export function getEffectiveIncludeInstallForContractDisplay(
  proposal: ProposalBuilderData,
  /** Used only when the proposal is not yet signed (matches public page local toggle). */
  localIncludeInstallWhenUnsigned: boolean,
): boolean {
  const signed = proposal.status === 'approved' || !!(proposal as unknown as { signature?: unknown }).signature;
  if (signed) {
    const installSel = (proposal as unknown as { installSelection?: boolean }).installSelection;
    if (typeof installSel === 'boolean') {
      return installSel;
    }
    const inst = (proposal.speciesPricing as { installTotal?: number } | undefined)?.installTotal;
    if (typeof inst === 'number') return inst > 0;
  }
  return !proposal.installOptional || localIncludeInstallWhenUnsigned;
}

/**
 * For CRM list rows: when a proposal is signed, prefer persisted commercial totals over `displayTotal`
 * (which can lag after admin amendments until the next full save).
 */
export function getSignedCommercialListTotal(proposal: ProposalBuilderData): number | null {
  const isSigned = proposal.status === 'approved' || !!(proposal as unknown as { signature?: unknown }).signature;
  if (!isSigned) return null;

  const bt = (proposal.speciesPricing as { billableTotal?: number } | undefined)?.billableTotal;
  if (typeof bt === 'number' && !Number.isNaN(bt) && bt > 0) {
    return Math.round(bt);
  }
  const gt = (proposal as unknown as { grandTotal?: number }).grandTotal;
  if (typeof gt === 'number' && !Number.isNaN(gt) && gt > 0) {
    return Math.round(gt);
  }

  const { selectedSpecies, kitSelectedSpecies } = parseSpeciesSelectionsForPublicTotals(proposal);
  const structureSelections = parseStructureSelectionsForPublicTotals(proposal);
  const includeInstall = getEffectiveIncludeInstallForContractDisplay(proposal, true);
  const g = computeProposalGrandTotals(proposal, {
    includeInstall,
    selectedSpecies,
    kitSelectedSpecies,
    structureSelections,
  });
  if (typeof g.total === 'number' && !Number.isNaN(g.total) && g.total > 0) {
    return Math.round(g.total);
  }
  return null;
}

/** Builder footer / public page display — sign-time snapshot, not live nodeSet math. */
export function getSignedContractFooterTotals(
  proposal: ProposalBuilderData,
): { design: number; timberKit: number; installWithTravel: number; bonding: number; discountAmount: number; total: number } | null {
  const isSigned = proposal.status === 'approved' || !!(proposal as unknown as { signature?: unknown }).signature;
  if (!isSigned) return null;

  const total = getSignedCommercialListTotal(proposal);
  if (total == null) return null;

  const sp = proposal.speciesPricing as (ProposalBuilderData['speciesPricing'] & {
    designTotal?: number;
    discountAmount?: number;
  }) | undefined;
  const includeInstall = getEffectiveIncludeInstallForContractDisplay(proposal, true);
  const timberKit = Math.round(sp?.timberTotal ?? proposal.timberKitTotal ?? 0);
  const installWithTravel = includeInstall ? Math.round(sp?.installTotal ?? proposal.installTotal ?? 0) : 0;
  const bonding = getSignedBondingTotal(proposal);
  // `proposal.designTotal` is a working cost field. A save after signing can replace the
  // billable design with the raw cost. Prefer the sign-time snapshot, then any stored
  // design that still adds up to the frozen total, otherwise back into design so the
  // lines match `speciesPricing.billableTotal`.
  const storedDesign = Math.round(
    typeof sp?.designTotal === 'number' ? sp.designTotal : (proposal.designTotal ?? 0),
  );
  const requestedDiscount = Math.round(
    typeof sp?.discountAmount === 'number'
      ? sp.discountAmount
      : getProposalDiscountAmount(proposal.discount),
  );
  const discount = Math.min(requestedDiscount, Math.max(0, storedDesign + timberKit + installWithTravel));
  const withDiscount = storedDesign + timberKit + installWithTravel + bonding - discount;
  const withoutDiscount = storedDesign + timberKit + installWithTravel + bonding;
  let design = storedDesign;
  let discountAmount = 0;
  if (Math.abs(withDiscount - total) <= 1) {
    discountAmount = discount;
  } else if (Math.abs(withoutDiscount - total) <= 1) {
    discountAmount = 0;
  } else {
    design = Math.max(0, total - timberKit - installWithTravel - bonding);
    discountAmount = 0;
  }

  return {
    design,
    timberKit,
    installWithTravel,
    bonding,
    discountAmount,
    total,
  };
}

/** Split a frozen bucket across structures. The last share absorbs rounding. */
export function allocateFrozenLine(weights: number[], target: number): number[] {
  if (weights.length === 0) return [];
  const roundedTarget = Math.round(target);
  if (weights.length === 1) return [roundedTarget];
  const sum = weights.reduce((acc, n) => acc + n, 0);
  if (sum <= 0) {
    const out = weights.map(() => 0);
    out[out.length - 1] = roundedTarget;
    return out;
  }
  const shares = weights.map(w => Math.round((w / sum) * roundedTarget));
  shares[shares.length - 1] += roundedTarget - shares.reduce((acc, n) => acc + n, 0);
  return shares;
}

/** Bonding premium frozen at signing; 0 for proposals signed without bonding. */
export function getSignedBondingTotal(proposal: ProposalBuilderData): number {
  const sp = proposal.speciesPricing as { bondingTotal?: number } | undefined;
  const v = sp?.bondingTotal ?? proposal.bondingTotal;
  return typeof v === 'number' && Number.isFinite(v) && v > 0 ? Math.round(v) : 0;
}

/** Same snapshot as footer, shaped for computeProposalGrandTotals consumers. */
export function getSignedGrandTotalsDisplay(
  proposal: ProposalBuilderData,
): { design: number; timberKit: number; install: number; travel: number; bonding: number; bondingPercent: number; discountAmount: number; total: number } | null {
  const signed = getSignedContractFooterTotals(proposal);
  if (!signed) return null;
  return {
    design: signed.design,
    timberKit: signed.timberKit,
    install: signed.installWithTravel,
    travel: 0,
    bonding: signed.bonding,
    bondingPercent: effectiveBondingPercent(proposal.globalCosts),
    discountAmount: signed.discountAmount,
    total: signed.total,
  };
}

export function calculateProposalNodeBuckets(nodeSet: ProposalNodeSet, profitMargin: number, proposal: ProposalBuilderData) {
  return calculateProposalNodeBucketsWithSelections(nodeSet, profitMargin, proposal);
}

export function calculateProposalNodeBucketsWithSelections(
  nodeSet: ProposalNodeSet,
  profitMargin: number,
  proposal: ProposalBuilderData,
  structureSelections?: Record<string, boolean>,
) {
  if (!isStructureClientSelected(nodeSet, structureSelections, proposalIsPhased(proposal))) {
    return { design: 0, timberKit: 0, install: 0, total: 0 };
  }

  const useDynamic = needsDynamicClientPricing(proposal, structureSelections);
  const selectedNodeSets = getClientVisibleNodeSets(proposal, structureSelections);

  if (!useDynamic && nodeSet.billablePrices) {
    return {
      design: nodeSet.billablePrices.design,
      timberKit: nodeSet.billablePrices.timberKit,
      install: nodeSet.billablePrices.install + nodeSet.billablePrices.travel,
      total: nodeSet.billablePrices.subtotal,
    };
  }

  const qty = nodeSet.quantity || 1;
  const totalGlobal = proposal?.globalCosts?.totalGlobalCosts ?? 0;
  const globalCostAddon = getGlobalCostAddonRawForNode(
    nodeSet,
    totalGlobal,
    proposal.nodeSets,
    selectedNodeSets.filter(ns => !ns.excludeFromGlobalPrices),
  );
  const projectOverheadPerStructure = getProjectOverheadPerStructure(proposal, selectedNodeSets);
  const receivesGlobalShare = !nodeSet.excludeFromGlobalPrices;

  const design = Math.round(applyProfitMargin(nodeSet.design.total, profitMargin) * qty);
  const timberKit = Math.round(
    applyProfitMargin(
      nodeSet.materials.total + nodeSet.production.total + nodeSet.freight.total + nodeSet.overhead.total,
      profitMargin,
    ) *
      qty +
      applyProfitMargin(receivesGlobalShare ? globalCostAddon : 0, profitMargin) +
      applyProfitMargin(projectOverheadPerStructure, profitMargin),
  );
  const installCombined = nodeSet.excludeInstall
    ? 0
    : Math.round(
        applyProfitMargin(
          nodeSet.install.total + nodeSet.travel.total + (nodeSet.install.overheadTotal || 0),
          profitMargin,
        ) * qty,
      );
  return { design, timberKit, install: installCombined, total: design + timberKit + installCombined };
}

/**
 * Timber-kit amount to show for one selected structure. Matches that structure's
 * share of `computeProposalGrandTotals().timberKit`.
 *
 * When the client drops an optional structure, global costs and project overhead
 * are recomputed across the structures that remain — do not use baked
 * `billablePrices.speciesOptions` from the all-structures save in that case.
 * Estimating (and design, when it is rolled into the kit) is included so a
 * single remaining structure's line equals the investment-summary timber kit.
 */
export function clientStructureTimberKitForDisplay(
  proposal: ProposalBuilderData,
  nodeSet: ProposalNodeSet,
  selectedSpecies: Record<string, string> | undefined,
  structureSelections?: Record<string, boolean>,
): number {
  const selected = getClientVisibleNodeSets(proposal, structureSelections);
  const buckets = calculateProposalNodeBucketsWithSelections(
    nodeSet,
    proposal.profitMargin,
    proposal,
    structureSelections,
  );
  const species = selectedSpecies?.[nodeSet.id];
  let timberKit = buckets.timberKit;
  if (species && species !== 'default') {
    const baked = nodeSet.billablePrices?.speciesOptions?.[species];
    if (!needsDynamicClientPricing(proposal, structureSelections) && baked !== undefined) {
      timberKit = baked;
    } else {
      timberKit = applySpeciesTimberKitBillableDelta(
        nodeSet,
        species,
        buckets.timberKit,
        proposal.profitMargin,
        nodeSet.quantity || 1,
      );
    }
  }
  if (proposal.designIncludedInKit) {
    timberKit += buckets.design;
  }
  const est = estimatingBillableForProposal(proposal);
  if (est > 0 && selected.length > 0) {
    const idx = selected.findIndex(ns => ns.id === nodeSet.id);
    if (idx >= 0) {
      const base = Math.floor(est / selected.length);
      const extra = idx === selected.length - 1 ? est - base * selected.length : 0;
      timberKit += base + extra;
    }
  }
  return timberKit;
}

export interface ProposalGrandTotalsInput {
  includeInstall: boolean;
  selectedSpecies: Record<string, string>;
  kitSelectedSpecies?: string | null;
  /** structureId -> included. Undefined = all client-visible structures included. */
  structureSelections?: Record<string, boolean>;
}

export interface ProposalGrandTotals {
  design: number;
  timberKit: number;
  install: number;
  travel: number;
  /** Promotional discount already subtracted from `total` (buckets unchanged). */
  discountAmount: number;
  /** Surety bonding pass-through already added to `total` (0 when the proposal has no bonding). */
  bonding: number;
  /** Percent used for `bonding` (0 when none). */
  bondingPercent: number;
  total: number;
}

/**
 * Canonical client-facing totals. The promotional discount is applied once
 * here (post-margin, clamped so total never goes negative), so the public
 * page, PDF, payment schedule, and sign-time snapshots all inherit it.
 * Bonding is then added as a pass-through percent of the discounted subtotal —
 * purely additive, so buckets and discount math are unchanged.
 */
export function computeProposalGrandTotals(
  proposal: ProposalBuilderData,
  input: ProposalGrandTotalsInput,
): ProposalGrandTotals {
  const base = computeUndiscountedGrandTotals(proposal, input);
  const requested = getProposalDiscountAmount(proposal.discount);
  const discountAmount = Math.min(requested, Math.max(0, base.total));
  const subtotal = base.total - discountAmount;
  const bondingPercent = effectiveBondingPercent(proposal.globalCosts);
  const bonding = computeBondingAmount(subtotal, bondingPercent);
  return {
    ...base,
    discountAmount,
    bonding,
    bondingPercent,
    total: subtotal + bonding,
  };
}

function estimatingBillableForProposal(proposal: ProposalBuilderData): number {
  return Math.round(
    applyProfitMargin(
      estimatingRaw(proposal.designEstimating, null, proposal.status),
      proposal.profitMargin,
    ),
  );
}

function applyEstimatingToPublicBuckets(
  proposal: ProposalBuilderData,
  buckets: { design: number; timberKit: number; install: number; travel: number; total: number },
  rollIntoKit: boolean,
): { design: number; timberKit: number; install: number; travel: number; total: number } {
  const est = estimatingBillableForProposal(proposal);
  if (est <= 0) return buckets;
  if (rollIntoKit) {
    return {
      ...buckets,
      timberKit: buckets.timberKit + est,
      total: buckets.total + est,
    };
  }
  return {
    ...buckets,
    design: buckets.design + est,
    total: buckets.total + est,
  };
}

function computeUndiscountedGrandTotals(
  proposal: ProposalBuilderData,
  { includeInstall, selectedSpecies, kitSelectedSpecies, structureSelections }: ProposalGrandTotalsInput,
): { design: number; timberKit: number; install: number; travel: number; total: number } {
  const shouldIncludeInstall = !proposal.installOptional || includeInstall;
  const selectedNodeSets = getClientVisibleNodeSets(proposal, structureSelections);

  if (proposal.isKitProposal && proposal.kitPricing && proposal.kitPricing.length > 0) {
    if (proposal.nodeSets.length > 1) {
      let totalKitBillable = 0;
      selectedNodeSets.forEach(ns => {
        const nsIndex = proposal.nodeSets.findIndex(n => n.id === ns.id);
        if (nsIndex < 0) return;
        const structureSpecies =
          selectedSpecies[ns.id] ||
          kitSelectedSpecies ||
          proposal.activeSpecies ||
          proposal.offeredSpecies?.[0] ||
          proposal.kitPricing?.[0]?.speciesName;
        const speciesPricing = proposal.kitPricing?.find(kp => kp.speciesName === structureSpecies);

        if (speciesPricing?.structurePricing && speciesPricing.structurePricing[nsIndex]) {
          totalKitBillable += speciesPricing.structurePricing[nsIndex].cost || 0;
        } else {
          totalKitBillable += (speciesPricing?.totalCost || 0) / proposal.nodeSets.length;
        }
      });

      let installRawTotal = 0;
      selectedNodeSets.forEach(ns => {
        const qty = ns.quantity || 1;
        installRawTotal += (ns.install.total + ns.travel.total) * qty;
      });
      const installBillable = Math.round(applyProfitMargin(installRawTotal, proposal.profitMargin));
      const installAmount = shouldIncludeInstall ? installBillable : 0;

      return applyEstimatingToPublicBuckets(
        proposal,
        {
          design: 0,
          timberKit: Math.round(totalKitBillable),
          install: installAmount,
          travel: 0,
          total: Math.round(totalKitBillable) + installAmount,
        },
        true,
      );
    }

    const activeSpecies =
      kitSelectedSpecies || proposal.activeSpecies || proposal.offeredSpecies?.[0] || proposal.kitPricing[0]?.speciesName;
    const selectedPricing = proposal.kitPricing.find(kp => kp.speciesName === activeSpecies);

    if (selectedPricing) {
      const kitBillable = selectedPricing.totalCost;
      let installRawTotal = 0;
      selectedNodeSets.forEach(ns => {
        const qty = ns.quantity || 1;
        installRawTotal += (ns.install.total + ns.travel.total) * qty;
      });
      const installBillable = Math.round(applyProfitMargin(installRawTotal, proposal.profitMargin));
      const installAmount = shouldIncludeInstall ? installBillable : 0;

      return applyEstimatingToPublicBuckets(
        proposal,
        {
          design: 0,
          timberKit: kitBillable,
          install: installAmount,
          travel: 0,
          total: kitBillable + installAmount,
        },
        true,
      );
    }
  }

  const rawTotals = selectedNodeSets.reduce(
    (acc, ns) => {
      const buckets = calculateProposalNodeBucketsWithSelections(
        ns,
        proposal.profitMargin,
        proposal,
        structureSelections,
      );
      const structureSelectedSpecies = selectedSpecies[ns.id];
      let timberKitAdjusted = buckets.timberKit;

      if (structureSelectedSpecies && structureSelectedSpecies !== 'default') {
        if (!needsDynamicClientPricing(proposal, structureSelections) &&
          ns.billablePrices?.speciesOptions?.[structureSelectedSpecies] !== undefined) {
          timberKitAdjusted = ns.billablePrices.speciesOptions[structureSelectedSpecies];
        } else {
          timberKitAdjusted = applySpeciesTimberKitBillableDelta(
            ns,
            structureSelectedSpecies,
            buckets.timberKit,
            proposal.profitMargin,
            ns.quantity || 1,
          );
        }
      }

      const installAmount = shouldIncludeInstall ? buckets.install : 0;

      return {
        design: acc.design + buckets.design,
        timberKit: acc.timberKit + timberKitAdjusted,
        install: acc.install + installAmount,
        travel: acc.travel + (ns.billablePrices?.travel || 0),
        total: acc.total + buckets.design + timberKitAdjusted + installAmount,
      };
    },
    { design: 0, timberKit: 0, install: 0, travel: 0, total: 0 },
  );

  const withEstimating = applyEstimatingToPublicBuckets(proposal, rawTotals, true);

  if (proposal.designIncludedInKit) {
    return {
      ...withEstimating,
      design: 0,
      timberKit: withEstimating.timberKit + withEstimating.design,
    };
  }

  return withEstimating;
}

export function parseSpeciesSelectionsForPublicTotals(p: ProposalBuilderData): {
  selectedSpecies: Record<string, string>;
  kitSelectedSpecies: string | null;
} {
  const raw = (p as unknown as { selectedSpecies?: unknown }).selectedSpecies;
  const selectedSpecies: Record<string, string> =
    raw && typeof raw === 'object' && !Array.isArray(raw) ? { ...(raw as Record<string, string>) } : {};
  const sp = (p as unknown as { speciesPricing?: { selections?: Record<string, string> } }).speciesPricing;
  if (sp?.selections && typeof sp.selections === 'object') {
    Object.assign(selectedSpecies, sp.selections);
  }
  let kitSelectedSpecies: string | null = null;
  if (p.isKitProposal && p.nodeSets.length === 1) {
    const nsId = p.nodeSets[0]?.id;
    kitSelectedSpecies =
      (nsId && selectedSpecies[nsId] ? selectedSpecies[nsId] : null) ||
      Object.values(selectedSpecies).find(v => !!v && v !== 'default') ||
      Object.values(selectedSpecies)[0] ||
      p.activeSpecies ||
      p.offeredSpecies?.[0] ||
      null;
  }
  return { selectedSpecies, kitSelectedSpecies };
}

/** Human-readable species label(s) frozen at client sign — for builder footer / cards. */
export function getSignedSpeciesDisplayLabel(proposal: ProposalBuilderData): string | null {
  const isSigned =
    proposal.status === 'approved' || !!(proposal as unknown as { signature?: unknown }).signature;
  if (!isSigned) return null;

  const { selectedSpecies, kitSelectedSpecies } = parseSpeciesSelectionsForPublicTotals(proposal);

  if (proposal.isKitProposal) {
    const name = kitSelectedSpecies || proposal.activeSpecies;
    return name?.trim() || null;
  }

  const labels = Array.from(
    new Set(
      Object.values(selectedSpecies)
        .map(v => (v === 'default' ? '' : v))
        .filter(Boolean),
    ),
  );
  if (labels.length === 1) return labels[0];
  if (labels.length > 1) return labels.join(', ');
  return null;
}

export function parseStructureSelectionsForPublicTotals(
  p: ProposalBuilderData,
): Record<string, boolean> | undefined {
  const raw = (p as unknown as { structureSelections?: unknown }).structureSelections;
  const fromTop =
    raw && typeof raw === 'object' && !Array.isArray(raw)
      ? { ...(raw as Record<string, boolean>) }
      : undefined;
  const sp = (p.speciesPricing as { structureSelections?: Record<string, boolean> } | undefined)
    ?.structureSelections;
  if (sp && typeof sp === 'object') {
    return { ...fromTop, ...sp };
  }
  return fromTop;
}

/** Count how many structures would remain selected if toggling one optional structure off. */
export function countClientSelectedStructures(
  proposal: ProposalBuilderData,
  structureSelections: Record<string, boolean>,
): number {
  const phased = proposalIsPhased(proposal);
  return (proposal.nodeSets || []).filter(ns =>
    isStructureClientSelected(ns, structureSelections, phased),
  ).length;
}

/** Default structure selections: all enabled structures included, exclusive structures normalized to one. */
export function buildDefaultStructureSelections(proposal: ProposalBuilderData): Record<string, boolean> {
  const selections: Record<string, boolean> = {};
  (proposal.nodeSets || []).forEach(ns => {
    if (ns.enabled !== false) {
      selections[ns.id] = true;
    }
  });
  return normalizeExclusiveGroupSelections(proposal.nodeSets || [], selections);
}
